import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CCard, CCardBody, CSpinner } from "@coreui/react";
import { RunInfoCard } from "@src/components/RunDetails/RunInfoCard";
import { RunBlock } from "@src/components/RunResults/RunBlock";
import { RunDetailProvider } from "@src/contexts/RunDetailContext";
import { useBacktestActions, useExecutionActions } from "@src/hooks/redux";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import type { ApiBacktest, ApiExecution } from "@tradingdroid/frontends-types";

interface Props {
  type: "backtest" | "execution";
}

export const RunDetailPage: React.FC<Props> = ({ type }) => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToastActions();

  const navigate = useNavigate();

  // Action dispatchers for Redux side-effect sync (list, portfolio benefit from fresh data)
  const { upsertExecution } = useExecutionActions();
  const { upsertBacktest } = useBacktestActions();

  const [run, setRun] = useState<T.ApiRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // updateRun: sets local state (source of truth) AND upserts to Redux as side-effect
  const updateRun = useCallback(
    (updatedRun: T.ApiRun) => {
      setRun(updatedRun);
      if (type === "execution") {
        upsertExecution(updatedRun as ApiExecution);
      } else {
        upsertBacktest(updatedRun as ApiBacktest);
      }
    },
    [type, upsertExecution, upsertBacktest],
  );

  // Fetch run with full embeds - local state is the source of truth for the detail page,
  // Redux upsert is a side-effect so other consumers (list, portfolio) get fresh data too
  const fetchRunData = useCallback(
    async (numId: number) => {
      const getFn = type === "execution" ? tdApi.getExecution : tdApi.getBacktest;
      const embed =
        type === "execution"
          ? "positions(movements(order)),job,live_metrics,execution_group"
          : "positions(movements(order)),job";
      const result = await getFn(numId, { embed });

      if (!result) {
        return null;
      }

      updateRun(result);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type],
  );

  // Fetch from API when id/type changes
  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("Missing run id in URL");
      return;
    }

    const numId = parseInt(id, 10);
    if (Number.isNaN(numId)) {
      setLoading(false);
      setError("Invalid run id");
      return;
    }

    // Reset state on id/type change
    setRun(null);
    setLoading(true);
    setError(null);

    fetchRunData(numId)
      .then((result) => {
        if (!result) {
          showToast({
            message: `${type} with ID ${id} not found`,
            type: "danger",
          });
          navigate(type === "execution" ? "/executions" : "/backtests");
        }
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        showToast({
          message: `Failed to load ${type}: ${message}`,
          type: "danger",
        });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]); // Only re-fetch when id or type changes

  const contextValue = useMemo(() => ({ run, type, updateRun }), [run, type, updateRun]);

  const renderLoadingBlock = () => (
    <CCard>
      <CCardBody className="text-center p-4">
        <CSpinner />
        <p className="mt-2">Loading {type} details...</p>
      </CCardBody>
    </CCard>
  );

  const renderErrorBlock = (err: string) => (
    <CCard>
      <CCardBody className="text-center p-4">
        <div className="text-danger mb-3">
          <strong>Error Loading Details</strong>
        </div>
        <p>{err}</p>
      </CCardBody>
    </CCard>
  );

  return (
    <RunDetailProvider value={contextValue}>
      <div className={`RunDetailPage runType-${type}`}>
        {loading ? (
          renderLoadingBlock()
        ) : (
          <>
            <RunInfoCard run={run} type={type} />

            {error && renderErrorBlock(error)}

            {run && run.positions && <RunBlock run={run} type={type} />}
          </>
        )}
      </div>
    </RunDetailProvider>
  );
};
